import { computed, ComputedRef, readonly, Ref, ref } from 'vue'

import { useTimer } from '@collector/shared-ui'
import {
  getValidMinuteWithSecond,
  MinuteWithSecond,
} from '@collector/shared-utils'

export type UseEventClock = {
  addTimerSecond: (value: number) => void
  /**
   * The current event time in minutes and seconds that can be displayed in the UI
   *
   * If the `maxMinute` is defined and the current minute is greater than or equal to the `maxMinute`, return the `maxMinute`.
   */
  eventTime: ComputedRef<MinuteWithSecond>
  /**
   * Init timer with minute and second, if `isClockRunning` - start counting.
   */
  initTimer: (minute: number, second: number) => void
  /**
   * Internal state to keep track of whether the clock is running.
   *
   * Note: This is separate state from the `event.clock_status` and uses it's value only once, when the clock is created.
   *
   * Internal state guarantees that the clock is in sync with pause/play button in UI and not affected by changes in the `event.clock_status`.
   */
  isClockRunning: Ref<boolean>
  minute: Readonly<Ref<number>>
  second: Readonly<Ref<number>>
  restartClock: (
    shouldClockRunning: boolean,
    minute: number,
    second: number,
  ) => void
  setMinuteSecond: (minute: number, second: number) => void
  /**
   * Start counting seconds, without changing `isClockRunning`
   */
  startTimer: () => void
  /**
   * Stop counting seconds, without changing `isClockRunning`
   */
  stopTimer: () => void
  /**
   * Start counting seconds, set `isClockRunning` to `true`
   */
  startClock: () => void
  /**
   * Stop counting seconds, set `isClockRunning` to `false`
   */
  stopClock: () => void
  /**
   * Update minute on clock.
   */
  updateTimerMinute: (minute: number) => void
  /**
   * Update second on clock.
   *
   * If the timer was running, stop it, update the second, then start timer again
   */
  updateTimerSecond: (second: number) => void
}

export type UseEventClockParams = {
  clockRunning: boolean
  isTimerDirectionDown: ComputedRef<boolean>
  maxMinute?: ComputedRef<number>
}

export function useEventClock({
  clockRunning,
  isTimerDirectionDown,
  maxMinute,
}: UseEventClockParams): UseEventClock {
  // composables
  const {
    elapsedSeconds,
    minute,
    second,
    startTimer,
    stopTimer,
    setMinute,
    setSecond,
    clearElapsedSeconds,
  } = useTimer()

  const isClockRunning = ref<boolean>(clockRunning)

  // methods
  function initTimer(minute: number, second: number): void {
    setMinute(minute)
    setSecond(second)

    if (isClockRunning.value) {
      startTimer()
    }
  }

  function stopClock(): void {
    stopTimer()
    isClockRunning.value = false
  }

  function startClock(): void {
    startTimer()
    isClockRunning.value = true
  }

  function restartClock(
    shouldClockRunning: boolean,
    minute: number,
    second: number,
  ): void {
    setMinuteSecond(minute, second)

    if (shouldClockRunning) {
      if (isClockRunning.value) {
        stopTimer()
        clearElapsedSeconds()
        startTimer()
      } else {
        startClock()
      }
    } else {
      stopClock()
      clearElapsedSeconds()
    }
  }

  function setMinuteSecond(minute: number, second: number): void {
    setMinute(minute)
    setSecond(second)
  }

  function updateTimerMinute(minute: number): void {
    setSecond(eventTime.value.second)
    setMinute(minute)

    clearElapsedSeconds()
  }

  function updateTimerSecond(second: number): void {
    if (isClockRunning.value) {
      stopTimer()
    }

    setMinute(eventTime.value.minute)
    setSecond(second)

    clearElapsedSeconds()

    if (isClockRunning.value) {
      startTimer()
    }
  }

  function addTimerSecond(value: number): void {
    if (isClockRunning.value) {
      stopTimer()
    }

    const { minute, second } = getValidMinuteWithSecond(
      eventTime.value.minute * 60 + eventTime.value.second + value,
      maxMinute?.value,
    )

    clearElapsedSeconds()
    setMinute(minute)
    setSecond(second)

    if (isClockRunning.value) {
      startTimer()
    }
  }

  // computed
  const eventTime = computed(() => {
    const elapsed = isTimerDirectionDown.value
      ? -elapsedSeconds.value
      : elapsedSeconds.value

    return getValidMinuteWithSecond(
      minute.value * 60 + second.value + elapsed,
      maxMinute?.value,
    )
  })

  return {
    minute: readonly(minute),
    second: readonly(second),
    addTimerSecond,
    eventTime,
    initTimer,
    isClockRunning,
    restartClock,
    setMinuteSecond,
    startClock,
    startTimer,
    stopClock,
    stopTimer,
    updateTimerMinute,
    updateTimerSecond,
  }
}
